<script>
    import modalService from '@/services/core/modal.service';
    import Modal from '@/components/modals/Modal';
    import utilsService from '@/services/core/utils.service';
    import httpService from '@/services/core/http.service';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import DxDateBox from 'devextreme-vue/date-box';
    import { DxSelectBox } from 'devextreme-vue/select-box';
    import DataSource from 'devextreme/data/data_source';

    export default {
        components: {
            Modal,
            ButtonSpinner,
            DxDateBox,
            DxSelectBox
        },
        props: {
            modalData: Object
        },
        data() {
            return {
                isLoading: 0,
                companiesLoaded: true,
                companiesDs: {},
                selectedCompany: null,
                // Compare one company's billing for an explicit period against what Freshbooks currently bills,
                // ignoring the "due" cycle — to validate a dispute or recover a period the daily missed.
                recovery: {
                    start: null,
                    end: null
                },
                lastInvoiceLoading: false,
                recoveryResult: null,
                // True once the corrected billing has been persisted (the Generate step).
                generated: false
            };
        },
        computed: {
            hasDiff() {
                const r = this.recoveryResult;
                if (!r) return false;
                const differs = (billed, shouldBe) => billed !== null && billed !== undefined && billed !== shouldBe;
                return differs(r.billedActiveProductionPoints, r.activeProductionPoints) ||
                    differs(r.billedInactiveProductionPoints, r.inactiveProductionPoints);
            },
            billedUnavailable() {
                const r = this.recoveryResult;
                return !!r && r.billedActiveProductionPoints == null && r.billedInactiveProductionPoints == null;
            },
            allMatch() {
                return !!this.recoveryResult && !this.hasDiff && !this.billedUnavailable;
            },
            // Only offer to persist when it is worth it: a discrepancy to correct, or no invoice to compare
            // against (a plain recovery). When everything matches there is nothing to generate.
            canGenerate() {
                return !!this.recoveryResult && !this.generated && (this.hasDiff || this.billedUnavailable);
            }
        },
        methods: {
            close() {
                modalService.closeModal();
            },
            save() {
                // The form's primary action is Compare (a preview — nothing is saved).
                this.runRecovery(false);
            },
            runGenerate() {
                this.runRecovery(true);
            },
            runRecovery(persist) {
                if (!this.selectedCompany) {
                    utilsService.showToast('Pick a company.');
                    return;
                }
                if (!this.recovery.start || !this.recovery.end) {
                    utilsService.showToast('Pick a period (from / to).');
                    return;
                }
                if (this.recovery.end < this.recovery.start) {
                    utilsService.showToast('The "to" date cannot be before the "from" date.');
                    return;
                }

                const dto = {
                    companyId: Number(this.selectedCompany.id),
                    start: this.toIsoDate(this.recovery.start),
                    end: this.toIsoDate(this.recovery.end),
                    persist
                };

                this.isLoading++;
                httpService
                    .post(`/company-billing-history/recovery`, dto)
                    .then(result => {
                        this.recoveryResult = result;
                        if (persist) {
                            this.generated = true;
                            utilsService.showToast('Corrected billing recorded.');
                            this.modalData.onSuccess && this.modalData.onSuccess();
                        }
                    })
                    .finally(() => this.isLoading--);
            },
            loadLastInvoiceDate() {
                this.recoveryResult = null;
                this.generated = false;
                this.lastInvoiceLoading = true;
                httpService
                    .get(`/company-billing-history/last-invoice-date/${Number(this.selectedCompany.id)}`)
                    .then(data => {
                        // The period the automatic run actually billed for: it ends the day BEFORE the
                        // invoice issued, and starts at the previous invoice. Reproducing that is the whole
                        // point of the comparison, so prefer it over deriving anything from the invoice date.
                        if (data && data.periodStart && data.periodEnd) {
                            this.recovery.start = new Date(data.periodStart);
                            this.recovery.end = new Date(data.periodEnd);
                            return;
                        }

                        // No invoice to anchor to: fall back to the last 30 days so the modal still opens.
                        const end = data && data.lastInvoiceDate ? new Date(data.lastInvoiceDate) : new Date();
                        const start = new Date(end);
                        start.setDate(start.getDate() - 30);
                        this.recovery.end = end;
                        this.recovery.start = start;
                    })
                    .finally(() => (this.lastInvoiceLoading = false));
            },
            toIsoDate(d) {
                const pad = n => String(n).padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            },
            billedText(billed) {
                return billed === null || billed === undefined ? '—' : billed;
            },
            diffText(billed, shouldBe) {
                if (billed === null || billed === undefined) return '—';
                const diff = shouldBe - billed;
                if (diff === 0) return '✓';
                return diff > 0 ? `+${diff}` : `${diff}`;
            },
            diffClass(billed, shouldBe) {
                if (billed === null || billed === undefined) return '';
                return shouldBe - billed === 0 ? 'text-success' : 'text-danger fw-bold';
            },
            loadCompanies() {
                this.selectedCompany = null;

                this.companiesLoaded = false;

                const url = `companies`;

                httpService
                    .get(url)
                    .then(data => {
                        this.companiesDs = new DataSource({
                            key: 'id',
                            store: data,
                            paginate: true,
                            pageSize: 10
                        });
                    })
                    .finally(() => (this.companiesLoaded = true));
            }
        },
        mounted() {
            this.loadCompanies();
        },
        watch: {
            selectedCompany() {
                this.recoveryResult = null;
                this.generated = false;
                if (this.selectedCompany) {
                    this.loadLastInvoiceDate();
                }
            },
            'recovery.start'() {
                this.recoveryResult = null;
                this.generated = false;
            },
            'recovery.end'() {
                this.recoveryResult = null;
                this.generated = false;
            }
        }
    };
</script>

<template>
    <Modal sizeClass="modal-md">
        <form class="gsb-form" @submit.prevent="save">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">Generate Billing</h3>
                </div>
                <div class="modal-body">
                    <p class="text-muted small mb-3">
                        Compare a company's billing for a period against what Freshbooks currently bills. If there is a
                        discrepancy (or no invoice to compare against), <strong>Generate</strong> records the corrected
                        billing — it does not push to Freshbooks.
                    </p>

                    <div class="row mb-3">
                        <label class="col-sm-3 col-form-label">Company</label>
                        <div class="col-sm">
                            <DxSelectBox
                                v-model="selectedCompany"
                                :data-source="companiesDs"
                                display-expr="name"
                                :search-enabled="true"
                                search-mode="contains"
                                search-expr="name"
                                :search-timeout="200"
                                drop-down-button-template="loadIcon"
                                placeholder="Pick a company"
                                :show-clear-button="true"
                            >
                                <template #loadIcon="{}">
                                    <div>
                                        <span v-show="!companiesLoaded" class="spinner-border spinner-border-sm me-2" role="status"></span>
                                    </div>
                                </template>
                            </DxSelectBox>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <label class="col-sm-3 col-form-label">From</label>
                        <div class="col-sm">
                            <DxDateBox v-model:value="recovery.start" type="date" />
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label class="col-sm-3 col-form-label">To</label>
                        <div class="col-sm">
                            <DxDateBox v-model:value="recovery.end" type="date" />
                            <small class="text-muted d-block mt-1">
                                <span v-if="lastInvoiceLoading">Loading the last invoice date…</span>
                                <span v-else>Defaults to the period the automatic billing used for the company's last invoice. Adjust if needed.</span>
                            </small>
                        </div>
                    </div>

                    <div v-if="recoveryResult" class="row mb-2">
                        <div class="col-sm-9 offset-sm-3">
                            <div class="border rounded p-2 bg-light">
                                <div class="fw-bold mb-2">Billed vs. should be</div>
                                <table class="table table-sm mb-2">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th class="text-end">Billed</th>
                                            <th class="text-end">Should be</th>
                                            <th class="text-end">Diff</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Active production points</td>
                                            <td class="text-end">{{ billedText(recoveryResult.billedActiveProductionPoints) }}</td>
                                            <td class="text-end">{{ recoveryResult.activeProductionPoints }}</td>
                                            <td class="text-end" :class="diffClass(recoveryResult.billedActiveProductionPoints, recoveryResult.activeProductionPoints)">
                                                {{ diffText(recoveryResult.billedActiveProductionPoints, recoveryResult.activeProductionPoints) }}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Inactive production points</td>
                                            <td class="text-end">{{ billedText(recoveryResult.billedInactiveProductionPoints) }}</td>
                                            <td class="text-end">{{ recoveryResult.inactiveProductionPoints }}</td>
                                            <td class="text-end" :class="diffClass(recoveryResult.billedInactiveProductionPoints, recoveryResult.inactiveProductionPoints)">
                                                {{ diffText(recoveryResult.billedInactiveProductionPoints, recoveryResult.inactiveProductionPoints) }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div class="small text-muted">
                                    Should-be breakdown: {{ recoveryResult.activeWellCount }} wells · {{ recoveryResult.activeBatteryCount }} batteries · {{ recoveryResult.activeUserCount }} users
                                </div>
                                <div v-if="billedUnavailable" class="small text-muted mt-1">
                                    Billed values unavailable — no Freshbooks profile for this company, or Freshbooks was not reachable.
                                </div>
                                <div v-else-if="hasDiff && !generated" class="small text-danger mt-1">
                                    Discrepancy found. Generate to record the corrected billing (then push to Freshbooks separately).
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer d-flex justify-content-between align-items-center">
                    <button type="button" class="btn" @click="close">Close</button>
                    <div class="d-flex align-items-center">
                        <span v-if="allMatch" class="text-success small me-2">No discrepancy — nothing to correct.</span>
                        <span v-if="generated" class="text-success small me-2">Corrected billing recorded.</span>
                        <ButtonSpinner v-if="!recoveryResult" :isLoading="isLoading" text="Compare" />
                        <button v-else-if="canGenerate" type="button" class="btn btn-primary" :disabled="isLoading > 0" @click="runGenerate">
                            Generate
                        </button>
                    </div>
                </div>
            </div>
        </form>
    </Modal>
</template>

<style lang="scss" scoped>
    .dx-selectbox {
        height: 32px;
        line-height: 1.3;
        font-size: 13px;
    }
</style>
